import {
  Badge,
  ButtonBox,
  Dialog as DialogBox,
  IconButtonNeutral,
  ReadOnlyTable,
  ReadOnlyTableColumn as Column,
  SegmentedSwitch,
  SimpleDropDown,
  Toast as ToastCustom,
  type IReadonlyTableRef,
  type IToastRef,
} from "@vm-synthia/sdk-react";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { linkAuthRoute } from "routes";
import {
  formattedDate,
  formattedDateHourMinute,
  getLocalStorage,
  setLocalStorage,
} from "util/CommonUtil";
import { LOCAL_STORAGE } from "util/Constant";
import PageUploadAudioController from "../../controller/PageUploadAudioController";
import pageUploadPDFReducer, {
  INITIAL_PAGE_UPLOAD_PDF_STATE,
} from "../../reducer/PageUploadPDFReducer";
import PageUploadAudioAPIService from "../../service/PageUploadAudioAPIService";
import UploadFormView from "./UploadFormView";
import {
  deriveWorkflowStatus,
  WORKFLOW_STATUS_UI,
  type StatusStepsMap,
  type WorkflowStatus,
} from "./useAudioWorkflowStatus";
// The hand-rolled WorkflowsListTable is retired in favor of the SDK's ReadOnlyTable
// (used directly below); WorkflowsListTable.tsx is kept around only for reference.
// import WorkflowsListTable, { type WorkflowListRow } from "./WorkflowsListTable";
import WorkflowStepsView from "./WorkflowStepsView";

interface UploadAudioResponseData {
  status?: string;
  message?: string;
  fileName?: string;
  url?: string;
  contentType?: string;
  size?: number;
  isPrivate?: boolean;
  workflowTriggered?: boolean;
}

/** Query param carrying the audio url of a triggered workflow to track in the dialog. */
const AUDIO_URL_PARAM = "audio";
/** Accepted alias for the same param (advertised by some entry points). */
const AUDIO_URL_ALIAS_PARAM = "audio-url";
/** Query param carrying the workflows table's current page (1-based), so it survives navigation. */
const TABLE_PAGE_PARAM = "page";

type WorkflowFilter = "all" | "completed" | "processing" | "error";

const FILTERS: Array<{ key: WorkflowFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "processing", label: "Processing" },
  { key: "error", label: "Failed" },
];

type AutoRefreshOption = "off" | "30s" | "1m" | "5m";

/** Background refresh cadence for the workflows table, keyed by the auto-refresh dropdown value. */
const AUTO_REFRESH_INTERVAL_MS: Record<AutoRefreshOption, number> = {
  off: 0,
  "30s": 30000,
  "1m": 60000,
  "5m": 300000,
};

const AUTO_REFRESH_OPTIONS: Array<{ label: string; value: AutoRefreshOption }> = [
  { label: "Auto refresh: Off", value: "off" },
  { label: "Auto refresh: 30s", value: "30s" },
  { label: "Auto refresh: 1 min", value: "1m" },
  { label: "Auto refresh: 5 min", value: "5m" },
];

const DEFAULT_AUTO_REFRESH: AutoRefreshOption = "30s";

/** Reads the user's last-picked auto-refresh cadence, falling back to the default. */
const getStoredAutoRefresh = (): AutoRefreshOption => {
  const stored = getLocalStorage(LOCAL_STORAGE.AUDIO_WORKFLOWS_AUTO_REFRESH);
  const isValidOption = AUTO_REFRESH_OPTIONS.some((option) => option.value === stored);
  return isValidOption ? (stored as AutoRefreshOption) : DEFAULT_AUTO_REFRESH;
};

const COLUMN_HEADER_TEXT_CLASS = "text-text-primary text-label-medium font-semibold bg-white";

/** One row of the audio workflows table, with its status already derived. */
interface WorkflowRow {
  id: string;
  fileUrl: string;
  fileName: string;
  status: Exclude<WorkflowStatus, "idle">;
  completedCount: number;
  totalCount: number;
  triggeredFrom?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Client-side status filter, applied to the page of rows the API just returned. */
const filterRowsByStatus = (rows: WorkflowRow[], filter: WorkflowFilter): WorkflowRow[] => {
  switch (filter) {
    case "completed":
      return rows.filter((row) => row.status === "succeeded" || row.status === "success");
    case "processing":
      return rows.filter((row) => row.status === "running" || row.status === "pending");
    case "error":
      return rows.filter((row) => row.status === "failed");
    default:
      return rows;
  }
};

/** Parse a query param from the live URL (fallback for host/remote setups where the router may not see it). */
const readQueryParam = (name: string): string => {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(name) ?? "";
};

/** Best-effort display name for a workflow row from its audio file url. */
const fileNameFromUrl = (fileUrl: string): string => {
  if (!fileUrl) return "Untitled audio";
  try {
    const path = new URL(fileUrl, window.location.origin).pathname;
    return decodeURIComponent(path.split("/").filter(Boolean).at(-1) ?? fileUrl);
  } catch {
    return fileUrl.split("/").filter(Boolean).at(-1) ?? fileUrl;
  }
};

const PageUploadAudio = () => {
  const [state, dispatch] = useReducer(pageUploadPDFReducer, INITIAL_PAGE_UPLOAD_PDF_STATE);
  // Keep one controller instance across renders so its polling timers survive;
  // refresh its state reference every render so reads are never stale.
  const controllerRef = useRef<PageUploadAudioController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new PageUploadAudioController(state, dispatch);
  } else {
    (controllerRef.current as any).state = state;
  }
  const controller = controllerRef.current;

  const toastMessageRef = useRef<IToastRef | null>(null);
  const tableRef = useRef<IReadonlyTableRef | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // An `audio` (or `audio-url`) query param means a workflow is being tracked: the
  // steps dialog is open and long-polling `/audio/progress`. The URL is the single
  // source of truth, so row clicks, uploads and reloads all share the same path.
  const audioUrlParam =
    searchParams.get(AUDIO_URL_PARAM) ||
    searchParams.get(AUDIO_URL_ALIAS_PARAM) ||
    readQueryParam(AUDIO_URL_PARAM) ||
    readQueryParam(AUDIO_URL_ALIAS_PARAM);

  // Table page to restore on mount (0-based for ReadOnlyTable's `defaultPage`),
  // read once from the URL so coming back to this page lands on the same table page.
  const [initialTablePage] = useState<number>(() => {
    const page = Number(searchParams.get(TABLE_PAGE_PARAM) || readQueryParam(TABLE_PAGE_PARAM));
    return Number.isInteger(page) && page > 1 ? page - 1 : 0;
  });
  const lastTablePageRef = useRef<number>(initialTablePage);

  const [filter, setFilter] = useState<WorkflowFilter>("all");
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshOption>(getStoredAutoRefresh);
  const [processingStartMessage, setProcessingStartMessage] = useState<string>("");

  // Which view the dialog shows: a tracked workflow's steps, the multi-upload job
  // logs, the upload form — or nothing.

  let dialogMode: "closed" | "form" | "steps" | "job" = "closed";

  if (audioUrlParam) {
    dialogMode = "steps";
  } else if (state.data.fileUploadUUID) {
    dialogMode = "job";
  } else if (state.data.showUploadModal) {
    dialogMode = "form";
  }

  // Keep the workflows table fresh while the page is mounted, at the cadence
  // chosen from the auto-refresh dropdown ("off" disables background polling;
  // the user can still refresh manually via the toolbar icon).
  useEffect(() => {
    const intervalMs = AUTO_REFRESH_INTERVAL_MS[autoRefresh];
    if (!intervalMs) {
      return;
    }
    const intervalId = setInterval(() => {
      tableRef.current?.search();
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [autoRefresh]);

  // Poll for job completion when a multi-file upload job is running.
  useEffect(() => {
    if (!state.data.fileUploadUUID || state.data.isProcessCompleted) {
      return;
    }

    controller.startJobPolling((jobResult) => {
      if (jobResult && jobResult.status === "completed") {
        toastMessageRef.current?.showFunction?.({
          message: "Processing and analysis completed successfully!",
          severity: "success",
        });
      } else if (jobResult && jobResult.status === "error") {
        toastMessageRef.current?.showFunction?.({
          message: "Error processing audio file. Please try again.",
          severity: "error",
        });
      }
    });

    return () => {
      controller.stopJobPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.fileUploadUUID, state.data.isProcessCompleted]);

  // Data source for the ReadOnlyTable: adapts its (start, limit, search) contract
  // to the page-based `/workflows/status` endpoint, deriving each row's overall
  // status with the same logic the steps dialog uses (deriveWorkflowStatus), so
  // list and popup always agree. The endpoint has no status filter, so `search`
  // (the segmented switch's current key) is applied client-side to the fetched page.
  const fetchWorkflowsPage = async (start: number, limit: number, search?: string) => {
    const page = Math.floor(start / limit) + 1;
    const { statuses, pagination } = await PageUploadAudioAPIService.listWorkflowStatuses(
      page,
      limit
    );

    const rows: WorkflowRow[] = statuses
      .filter((record) => record.fileUrl)
      .map((record) => {
        const summary = deriveWorkflowStatus((record.steps as StatusStepsMap) || {});
        return {
          id: record.id || record.fileUrl || "",
          fileUrl: record.fileUrl || "",
          fileName: fileNameFromUrl(record.fileUrl || ""),
          status: summary.status,
          completedCount: summary.completedCount,
          totalCount: summary.totalCount,
          triggeredFrom: record.triggeredFrom,
          createdAt: record.createdAt,
          updatedAt: record.updatedAt || record.createdAt,
        };
      });

    return {
      data: {
        results: filterRowsByStatus(rows, (search as WorkflowFilter) || "all"),
        totalRecords: pagination.totalCount,
      },
    };
  };

  const sortedProcessingLogs = useMemo(
    () =>
      [...state.data.processingLogs].sort(
        (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
      ),
    [state.data.processingLogs]
  );

  // Mirror the table's page into the URL (page 1 is left implicit). Called by
  // ReadOnlyTable on every fetch, so skip the navigation when nothing changed.
  const handleTablePageChange = (pageIndex: number) => {
    if (pageIndex === lastTablePageRef.current) return;
    lastTablePageRef.current = pageIndex;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (pageIndex > 0) next.set(TABLE_PAGE_PARAM, String(pageIndex + 1));
        else next.delete(TABLE_PAGE_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  const setAudioUrlParam = (url: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(AUDIO_URL_PARAM, url);
        next.delete(AUDIO_URL_ALIAS_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  const clearAudioUrlParam = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(AUDIO_URL_PARAM);
        next.delete(AUDIO_URL_ALIAS_PARAM);
        return next;
      },
      { replace: true }
    );
    // Host/remote fallback: when the router doesn't own these params, strip them
    // from the live URL too so the dialog actually closes.
    if (readQueryParam(AUDIO_URL_PARAM) || readQueryParam(AUDIO_URL_ALIAS_PARAM)) {
      const url = new URL(window.location.href);
      url.searchParams.delete(AUDIO_URL_PARAM);
      url.searchParams.delete(AUDIO_URL_ALIAS_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  };

  // Any row opens the steps dialog, whatever its status; completed workflows get
  // the "Go to Audio Analysis" button inside it.
  const handleRowClick = (row: WorkflowRow) => {
    setProcessingStartMessage("");
    setAudioUrlParam(row.fileUrl);
  };

  const handleOpenUpload = () => {
    controller.handleFilesChange([]);
    controller.setShowUploadModal(true);
  };

  const handleFormCancel = () => {
    controller.handleFilesChange([]);
    controller.setShowUploadModal(false);
  };

  const handleStepsClose = () => {
    clearAudioUrlParam();
    setProcessingStartMessage("");
    // Refresh once so the list reflects the tracked workflow's latest status.
    tableRef.current?.search();
  };

  // Close the multi-upload job dialog and clear the job context.
  const handleJobClose = () => {
    controller.stopJobPolling();
    controller.setFileUploadUUID("");
    controller.setIsProcessCompleted(false);
    controller.setProcessingLogs([]);
    controller.setCompletedPdfName("");
    controller.setCompletedPdfNames([]);
    controller.handleFilesChange([]);
    controller.setShowUploadModal(false);
    tableRef.current?.search();
  };

  const handleDialogHide = () => {
    if (dialogMode === "steps") handleStepsClose();
    else if (dialogMode === "job") handleJobClose();
    else handleFormCancel();
  };

  const handleViewJobResults = () => {
    const pdfNames =
      state.data.completedPdfNames.length > 0
        ? state.data.completedPdfNames
        : [state.data.completedPdfName];
    const pdfParam = pdfNames.join(",");
    navigate(
      linkAuthRoute + `/rebate-analysis?pdf=${pdfParam}&job_id=${state.data.fileUploadUUID}`
    );
  };

  const handleAddFiles = (incoming: FileList | File[]) => {
    const files = Array.from(incoming);

    // Filter to only audio files
    const audioFiles = files.filter((file) => {
      const isAudioMime = file.type?.startsWith("audio/");
      const isAudioExt = /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name);
      return isAudioMime || isAudioExt;
    });

    if (audioFiles.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "Invalid file type. Please upload Audio files only.",
        severity: "error",
      });
      return;
    }

    if (audioFiles.length < files.length) {
      toastMessageRef.current?.showFunction?.({
        message: `Only ${audioFiles.length} of ${files.length} files are audio. Non-audio files were ignored.`,
        severity: "info",
      });
    }

    // Append new files to existing selection (avoid duplicates by name)
    const existingFiles = state.data.selectedFiles;
    const existingNames = new Set(existingFiles.map((f) => f.name));
    const newFiles = audioFiles.filter((file) => !existingNames.has(file.name));

    if (newFiles.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "All selected files are already in the list.",
        severity: "info",
      });
      return;
    }

    if (newFiles.length < audioFiles.length) {
      toastMessageRef.current?.showFunction?.({
        message: `${audioFiles.length - newFiles.length} file(s) already selected. Added ${newFiles.length} new file(s).`,
        severity: "info",
      });
    }

    controller.handleFilesChange([...existingFiles, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    controller.handleFilesChange(state.data.selectedFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    const filesToUpload = state.data.selectedFiles;

    if (filesToUpload.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "No files selected. Add audio from your device.",
        severity: "error",
      });
      return;
    }

    try {
      controller.setProcessingLogs([]);
      setProcessingStartMessage("");

      // A single local audio file triggers the analysis workflow via the upload
      // endpoint; the dialog then flips to the steps view tracking `/audio/progress`.
      if (filesToUpload.length === 1) {
        const response = await PageUploadAudioAPIService.uploadAudio(filesToUpload[0], dispatch);
        const data: UploadAudioResponseData | undefined = response?.data;
        const uploadedUrl = data?.url;
        if (data?.workflowTriggered) {
          setProcessingStartMessage(data?.message || "Audio uploaded; processing started.");
        }
        toastMessageRef.current?.showFunction?.({
          message: data?.message || "Audio uploaded successfully.",
          severity: "success",
        });
        if (uploadedUrl) {
          controller.handleFilesChange([]);
          controller.setShowUploadModal(false);
          setAudioUrlParam(uploadedUrl);
        }
        // Without a url we cannot track the workflow: stay on the form and
        // rely on the toast above.
        return;
      }

      // Multiple files go through the job-based flow; the dialog shows its logs.
      const response = await controller.handleUploadAndAnalyzeMultiple(filesToUpload);
      if (response?.data) {
        toastMessageRef.current?.showFunction?.({
          message: `${filesToUpload.length} file(s) uploaded. Processing and analyzing...`,
          severity: "success",
        });
        controller.handleFilesChange([]);
        controller.setShowUploadModal(false);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Error uploading files";
      toastMessageRef.current?.showFunction?.({
        message: `Error: ${errorMessage}`,
        severity: "error",
      });
    }
  };

  const dialogTitles: Record<typeof dialogMode, string | null> = {
    closed: null,
    form: "Upload Audio",
    steps: "Workflow Progress",
    job: "Processing Audio Files",
  };

  return (
    <div
      className="relative w-full overflow-auto p-3"
      style={{ height: "calc(100vh - 40px)", background: "var(--color-surface-secondary)" }}
    >
      <div
        className="mx-auto flex h-full w-full flex-col p-6"
        style={{
          background: "var(--color-surface-primary)",
          border: "1px solid var(--color-border-weak)",
        }}
      >
        {/* Filter tabs + refresh controls + upload action */}
        <div className="mb-4 flex items-center justify-between">
          <SegmentedSwitch
            options={FILTERS.map((item) => ({ label: item.label, value: item.key }))}
            value={filter}
            onChange={(value) => setFilter(value as WorkflowFilter)}
            size="small"
          />
          <div className="flex items-center gap-2">
            <IconButtonNeutral
              icon={<i className="pi pi-refresh" />}
              onClick={() => tableRef.current?.search()}
              size="xSmall"
            />
            <div className="w-[168px]">
              <SimpleDropDown
                value={autoRefresh}
                options={AUTO_REFRESH_OPTIONS}
                optionLabel="label"
                optionValue="value"
                hideLabel
                hideErrorRow
                size="small"
                onChange={(e: any) => {
                  setAutoRefresh(e.value);
                  setLocalStorage(LOCAL_STORAGE.AUDIO_WORKFLOWS_AUTO_REFRESH, e.value);
                }}
              />
            </div>
            <ButtonBox
              label="Upload Audio"
              variant="primary"
              size="small"
              onClick={handleOpenUpload}
              className="!bg-purple-600 hover:!bg-purple-700"
            />
          </div>
        </div>

      
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ReadOnlyTable
            ref={tableRef}
            standardDataFormatPromise={fetchWorkflowsPage}
            search={filter}
            autoTableUpdate
            defaultPage={initialTablePage}
            onPageChange={handleTablePageChange}
            onRowClick={(event: { data?: WorkflowRow }) => {
              if (!event?.data) return;
              handleRowClick(event.data);
            }}
            paginator
            scrollable
            rowSize="small"
            className="h-full"
            classNameForHeader="small"
            containerClassName="flex-1 overflow-hidden"
            dataKey="id"
            style={{ width: "100%" }}
          >
            <Column
              header="File"
              body={(row: WorkflowRow) => (
                <span
                  className="text-text-primary text-sm block truncate"
                  title={row.fileUrl}
                >
                  {row.fileName}
                </span>
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "320px" }}
            />
            <Column
              header="Steps"
              body={(row: WorkflowRow) => (
                <span className="text-text-secondary text-body-small">
                  {row.completedCount}/{row.totalCount}
                </span>
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "120px" }}
            />
            <Column
              header="Status"
              body={(row: WorkflowRow) => (
                <Badge
                  label={WORKFLOW_STATUS_UI[row.status].label}
                  variant="neutral"
                  state={WORKFLOW_STATUS_UI[row.status].badgeState}
                  size="Small"
                  statusDot={row.status === "running"}
                />
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "140px" }}
            />
            <Column
              header="Triggered From"
              body={(row: WorkflowRow) => (
                <span className="text-text-secondary text-body-small">
                  {row.triggeredFrom || "—"}
                </span>
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "140px" }}
            />
            <Column
              header="Created"
              body={(row: WorkflowRow) => (
                <span className="text-text-secondary text-body-small">
                  {row.createdAt ? formattedDateHourMinute(row.createdAt) : "—"}
                </span>
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "160px" }}
            />
            <Column
              header="Updated"
              body={(row: WorkflowRow) => (
                <span className="text-text-secondary text-body-small">
                  {row.updatedAt ? formattedDate(row.updatedAt) : "—"}
                </span>
              )}
              headerClassName={COLUMN_HEADER_TEXT_CLASS}
              style={{ width: "160px" }}
            />
          </ReadOnlyTable>
        </div>
      </div>

      {/* Upload / steps / job dialog */}
      {/* PrimeReact renders an (empty, transparent) header by default, which leaves the
          white content box with square top corners; the dialog title is our own h2. */}
      <DialogBox visible={dialogMode !== "closed"} onHide={handleDialogHide} showHeader={false}>
        <div className="flex flex-col px-6 py-5" style={{ minWidth: 460, maxWidth: 560 }}>
          <div className="mb-4 flex items-center justify-between border-b border-border-weakest pb-3">
            <h2 className="text-heading-small text-text-primary">{dialogTitles?.[dialogMode]}</h2>
            <IconButtonNeutral
              icon={<i className="pi pi-times" />}
              onClick={handleDialogHide}
              size="xSmall"
            />
          </div>

          {dialogMode === "form" && (
            <UploadFormView
              selectedFiles={state.data.selectedFiles}
              isUploading={state.data.isUploading}
              onAddFiles={handleAddFiles}
              onRemoveFile={handleRemoveFile}
              onCancel={handleFormCancel}
              onUpload={handleUpload}
            />
          )}

          {/* Conditionally mounted so the 3s `/audio/progress` poll starts and
              stops with the dialog. */}
          {dialogMode === "steps" && (
            <WorkflowStepsView
              audioUrl={audioUrlParam}
              processingStartMessage={processingStartMessage}
              onClose={handleStepsClose}
            />
          )}

          {dialogMode === "job" && (
            <>
              {!state.data.isProcessCompleted ? (
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-50 p-3">
                  <i className="pi pi-spin pi-spinner text-blue-700" />
                  <span className="text-sm font-semibold text-blue-700">
                    Processing audio files and analyzing...
                  </span>
                </div>
              ) : (
                (state.data.completedPdfNames.length > 0 || state.data.completedPdfName) && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg bg-green-50 p-3">
                    <i className="pi pi-check-circle text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      Processing and analysis completed successfully!
                      {state.data.completedPdfNames.length > 1 &&
                        ` (${state.data.completedPdfNames.length} audio files)`}
                    </span>
                  </div>
                )
              )}

              {/* Processing logs */}
              <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50">
                <div className="shrink-0 border-b bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">
                  Processing Logs
                </div>
                <div className="max-h-[min(40vh,360px)] min-h-[80px] flex-1 overflow-y-auto overscroll-contain p-3">
                  {sortedProcessingLogs.length === 0 ? (
                    <span className="text-xs text-gray-500">Waiting for logs…</span>
                  ) : (
                    sortedProcessingLogs.map((log, idx) => {
                      const getLogColor = (level: string) => {
                        switch (level) {
                          case "success":
                            return "text-green-700";
                          case "error":
                            return "text-red-700";
                          case "warning":
                            return "text-yellow-700";
                          default:
                            return "text-gray-700";
                        }
                      };

                      const getLogIcon = (level: string) => {
                        switch (level) {
                          case "success":
                            return "✓";
                          case "error":
                            return "✗";
                          case "warning":
                            return "⚠";
                          default:
                            return "•";
                        }
                      };

                      return (
                        <div
                          key={`${log.timestamp}-${idx}-${log.message.slice(0, 24)}`}
                          className="flex items-start gap-2 border-b border-gray-100 py-2 text-sm last:border-b-0 last:pb-0"
                        >
                          <span className={`font-semibold ${getLogColor(log.level)}`}>
                            {getLogIcon(log.level)}
                          </span>
                          <span className={`flex-1 ${getLogColor(log.level)}`}>{log.message}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-1.5">
                <ButtonBox
                  label={state.data.isProcessCompleted ? "Close" : "Run in background"}
                  variant="secondary"
                  size="small"
                  onClick={handleJobClose}
                />
                {state.data.isProcessCompleted &&
                  (state.data.completedPdfNames.length > 0 || state.data.completedPdfName) && (
                    <ButtonBox
                      label="View Analysis Results"
                      variant="primary"
                      size="small"
                      onClick={handleViewJobResults}
                      className=""
                    />
                  )}
              </div>
            </>
          )}
        </div>
      </DialogBox>

      <ToastCustom ref={toastMessageRef} position="bottom-right" />
    </div>
  );
};

export default PageUploadAudio;
